import React, { useMemo } from "react";
import { deepPurple } from "@mui/material/colors";
import chroma from "chroma-js";

import { GaugeFrame, IconGlyph } from "./RadialGauge.styles";

const HOME_ICON_PATH = "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z";

interface RadialGaugeProps {
  min: number;
  max: number;
  value: number;
  width?: number;
  segments?: number;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Converts a polar coordinate (radius + angle in degrees, where 0deg points right and 90deg points up)
 * around the given center into an SVG cartesian point.
 */
const polarToCartesian = (cx: number, cy: number, radius: number, angleDeg: number): Point => {
  const angleRad = (angleDeg * Math.PI) / 180;
  return { x: cx + radius * Math.cos(angleRad), y: cy - radius * Math.sin(angleRad) };
};

/**
 * Builds the SVG path for a single donut-ring wedge spanning `startAngle` to `endAngle`.
 */
const wedgePath = (cx: number, cy: number, outerRadius: number, innerRadius: number, startAngle: number, endAngle: number): string => {
  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 0 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 0 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
};

/**
 * A half-circle gauge made of many thin colored segments (light to dark purple, left to right),
 * with a needle pointing at `value`'s position between `min` and `max`.
 */
const RadialGauge: React.FC<RadialGaugeProps> = ({ min, max, value, width = 220, segments = 20 }) => {
  const height = width / 2 + 16;
  const cx = width / 2;
  const cy = height - 16;
  const outerRadius = width / 2 - 6;
  const thickness = outerRadius * 0.24;
  const innerRadius = outerRadius - thickness;
  const gapRatio = 0.12;
  const needleLength = innerRadius * 0.8;

  const range = Math.max(max - min, 1);
  // Clamp the value to the min/max range so that the needle doesn't go outside the gauge.
  const clampedValue = Math.min(Math.max(value, min), max);
  const needleAngle = 180 - (180 * (clampedValue - min)) / range;
  const needleTip = polarToCartesian(cx, cy, needleLength, needleAngle);

  const iconSize = width * 0.1;
  const iconCenterY = cy - 20;

  const colors = useMemo(() => chroma.scale([deepPurple[100], deepPurple[900]]).colors(segments), [segments]);

  const wedges = useMemo(() => {
    const step = 180 / segments;
    const gap = step * gapRatio;
    return Array.from({ length: segments }, (_, index) => {
      const startAngle = 180 - index * step - gap / 2;
      const endAngle = 180 - (index + 1) * step + gap / 2;
      return {
        path: wedgePath(cx, cy, outerRadius, innerRadius, startAngle, endAngle),
        color: colors[index],
      };
    });
  }, [cx, cy, outerRadius, innerRadius, segments, colors]);

  return (
    <GaugeFrame gaugeWidth={width} gaugeHeight={height}>
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        {wedges.map(({ path, color }) => (
          <path key={path} d={path} fill={color} />
        ))}
        <line x1={cx} y1={cy} x2={needleTip.x} y2={needleTip.y} stroke="#2b2b2b" strokeWidth={3} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={7} fill="#2b2b2b" />
        <IconGlyph
          aria-hidden="true"
          focusable="false"
          x={cx - iconSize / 2}
          y={iconCenterY - iconSize / 2}
          width={iconSize}
          height={iconSize}
          viewBox="0 0 24 24"
        >
          <path d={HOME_ICON_PATH} fill="#2b2b2b" />
        </IconGlyph>
      </svg>
    </GaugeFrame>
  );
};

export default RadialGauge;
